import { createSelector } from "reselect"
import { get, includes, map, find, chain, orderBy, size, isEmpty, filter, findLastIndex } from "lodash"
import moment from "moment"

const EMPTY_ARRAY = []

const featuredFilter = ({ featuredStart, featuredEnd }) => !isEmpty(featuredStart) && (isEmpty(featuredEnd) || moment(featuredEnd).isSameOrAfter(undefined, "day"))

export const getDatasets = state => get(state, "global.data.accountDatasets.data", EMPTY_ARRAY)
export const getAllDatasets = state => get(state, "global.data.datasets.data", EMPTY_ARRAY)

export const getAccountDatasets = createSelector(
  [getDatasets],
  (datasets) => orderBy(datasets, ({ expiration }) => moment(expiration).format("x"), ["desc"]),
)

export const getBrowseDatasets = createSelector(
  [getAccountDatasets, getAllDatasets],
  (account, { bundles: rawB, datasets: rawD }) => {
    const bundles = mapDatasetsToBundles(rawB, account, rawD)
    const datasets = mapDatasetsToAccount(rawD, account)
    const all = combineAndOrderByFeatured(bundles, datasets)

    const lastFeaturedIndex = findLastIndex(all, featuredFilter)
    // No featured
    if (lastFeaturedIndex === -1) {
      return chain(all)
        .map(addFeaturedFn(false))
        .orderBy(["name"], ["asc"])
        .value()
    }

    // canBeFeatured doesn't have a limit
    // featured can only be 3
    // notFeatured is whatever didn't get randomly picked
    // canBeFeatured = featured + notFeatured
    const canBeFeatured = all.splice(0, lastFeaturedIndex + 1)
    const [featured, notFeatured] = randomizeFeatured(canBeFeatured)
    // Add up to 3 randomized featured dataset/bundle at the start of the list
    return [
      ...map(featured, addFeaturedFn(true)),
      ...chain([...notFeatured, ...all])
        .map(addFeaturedFn(false))
        .orderBy(["name"], ["asc"]),
    ]
  },
)

function mapDatasetsToAccount(datasets, accountDatasets) {
  if (size(accountDatasets) === 0) {
    return datasets
  }
  return map(datasets, (dataset) => find(accountDatasets, ({ id }) => dataset.id === id) || dataset)
}

function mapDatasetsToBundles(bundles, accountD, rawD) {
  const accountKeys = map(accountD, ({ id }) => id)

  return map(bundles, (bundle) => {
    const datasets = map(bundle.datasets, (datasetId) => {
      // Figure out if were pulling data from account datasets or normal datasets
      const col = includes(accountKeys, datasetId) ? accountD : rawD
      return find(col, ({ id }) => datasetId === id)
    })

    // Also map featuredStart and featuredEnd according to the internal datasets
    return ({
      ...bundle,
      datasets,
      ...chain(datasets)
        .filter(featuredFilter)
        .orderBy(({ featuredStart }) => moment(featuredStart).format("x"), ["desc"])
        .first()
        .pick(["featuredStart", "featuredEnd"])
        .value(),
    })
  })
}

function combineAndOrderByFeatured(bundles, datasets) {
  const collection = [...bundles, ...filter(datasets, ({ standalone }) => standalone)]
  return orderBy(collection, ({ featuredStart, featuredEnd }) => {
    if ((!isEmpty(featuredEnd) && moment(featuredEnd).isBefore(undefined, "day")) || isEmpty(featuredStart)) {
      return "0"
    }
    return moment(featuredStart).format("x")
  }, ["desc"])
}

function addFeaturedFn(value) {
  return (obj) => ({ ...obj, featured: value })
}

function randomizeFeatured(featured, n = 3, result = []) {
  // No need to randomize if max possible featured == n
  if (isEmpty(result) && size(featured) <= n) { return [featured, []] }

  const index = Math.floor(Math.random() * (featured.length - 1))
  result.push(featured.splice(index, 1)[0])
  if (result.length === n) {
    return [result, featured]
  } else {
    return randomizeFeatured(featured, n, result)
  }
}
